import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, forkJoin } from 'rxjs';
import { ContentTemplate, ContentTemplatesCollection, ContentTemplateText } from '../../shared/models/content-templates';
import { AllRequestParams, ContentTemplateService } from '../../shared/services/content-template.service';
import { LocalStorageService } from '../../shared/services/localStorage.service';
import { NotificationService } from '../../shared/services/notification.service';
import { TemplatesService } from '../../shared/services/templates.service';

@Component({
  standalone: false,
  selector: 'app-content-templates-list',
  templateUrl: './content-templates-list.component.html',
  styleUrls: ['./content-templates-list.component.scss'],
})
export class ContentTemplatesListComponent implements OnInit {
  MAX_TOTAL_ITEMS_COUNT = 1000;

  MAX_FAVORITES = 7;

  @Output() selectedChange = new EventEmitter<ContentTemplate[]>();

  @Input() selected: ContentTemplate[] = [];

  sizes = [5, 10, 15];
  size = 5;
  page = 1;

  favoriteRows: ContentTemplate[] = [];
  nonFavoriteRows: ContentTemplate[] = [];
  totalCountRows = 0;

  loading = false;
  isAllFavoritesSelected = false;
  isAllTemplateSelected = false;

  searchFormControl: FormControl = new FormControl();

  private requestParams = new AllRequestParams();

  constructor(
    public contentTemplateService: ContentTemplateService,
    public notificationService: NotificationService,
    public storage: LocalStorageService,
    public templateService: TemplatesService
  ) {
    this.size = storage.get('sender-list-size', this.size);
  }

  ngOnInit() {
    this.update();
    this.searchFormControl.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe(_ => {
      this.requestParams.search = _;
      this.page = 1;
      this.update();
    });
  }

  update() {
    this.loading = true;
    this.requestParams.size = this.size;
    this.requestParams.page = this.page - 1;
    this.requestParams.withText = true;

    const requests = [];
    requests.push(this.contentTemplateService.all(this.requestParams));
    if (!(this.requestParams.search?.length > 0 || this.requestParams.page > 0)) {
      requests.push(this.contentTemplateService.getFavourites());
    }

    forkJoin(requests).subscribe({
      next: results => this.handleContentTemplateSuccess(results),
      error: err => this.handleContentTemplateError(err),
    });
  }

  private handleContentTemplateSuccess(results: any[]): void {
    const contentTemplates = results[0] as ContentTemplatesCollection;
    const contentTemplatesFav = results.length > 1 ? (results[1] as ContentTemplate[]) : [];

    const favIds = new Set<string>();
    contentTemplatesFav.forEach(ctFav => {
      ctFav.smsTemplateTextDtos.forEach(ctFavText => {
        ctFavText.userFavourite = true;
        favIds.add(ContentTemplateService.generateTextId(ctFav, ctFavText));
      });
    });

    contentTemplates.content.forEach(ct => {
      ct.smsTemplateTextDtos = ct.smsTemplateTextDtos.filter(ctText => !favIds.has(ContentTemplateService.generateTextId(ct, ctText)));
    });
    contentTemplates.content = contentTemplates.content.filter(ct => ct.smsTemplateTextDtos.length > 0);

    const selectedIds = new Set<string>();
    this.selected.forEach(ct => {
      ct.smsTemplateTextDtos.filter(text => text.selected).forEach(text => selectedIds.add(ContentTemplateService.generateTextId(ct, text)));
    });

    if (selectedIds.size) {
      this.isAllTemplateSelected = this.markSelected(contentTemplates.content, selectedIds);
      this.isAllFavoritesSelected = this.markSelected(contentTemplatesFav, selectedIds);
    }

    this.favoriteRows = contentTemplatesFav;
    this.nonFavoriteRows = contentTemplates.content;
    this.totalCountRows = contentTemplates.totalElements;
    this.loading = false;
  }

  private markSelected(contentTemplates: ContentTemplate[], selectedIds: Set<string>): boolean {
    let isAllSelected = true;

    contentTemplates.forEach(ct => {
      let selectedCount = 0;

      ct.smsTemplateTextDtos.forEach(text => {
        const id = ContentTemplateService.generateTextId(ct, text);
        text.selected = selectedIds.has(id);
        if (text.selected) selectedCount++;
      });

      ct.selected = selectedCount > 0;
      if (!ct.selected) isAllSelected = false;
    });

    return isAllSelected;
  }

  private handleContentTemplateError(err: any): void {
    this.notificationService.error({
      title: 'Content Templates',
      message: 'An error occurred while getting content templates.',
      serviceName: 'NTC',
      requestMessage: err.statusText,
      requestCode: err.status,
      ts: err.timestamp ?? null,
    });
    this.loading = false;
  }

  onClickFavoriteSelectAll(): void {
    this.isAllFavoritesSelected = !this.isAllFavoritesSelected;
    this.favoriteRows.forEach(ct => {
      ct.selected = this.isAllFavoritesSelected;
      ct.smsTemplateTextDtos.forEach(text => (text.selected = this.isAllFavoritesSelected));
    });
    if (!this.isAllFavoritesSelected) {
      this.favoriteRows.forEach(ct => this.markUnselect(ct));
    }
    this.detectChanges();
  }

  onClickSelectAll(): void {
    this.isAllTemplateSelected = !this.isAllTemplateSelected;
    this.nonFavoriteRows.forEach(ct => {
      ct.selected = this.isAllTemplateSelected;
      ct.smsTemplateTextDtos.forEach(text => (text.selected = this.isAllTemplateSelected));
    });
    if (!this.isAllTemplateSelected) {
      this.nonFavoriteRows.forEach(ct => this.markUnselect(ct));
    }
    this.detectChanges();
  }

  onFavRowClick(contentTemplate: ContentTemplate): void {
    contentTemplate.selected = !contentTemplate.selected;
    contentTemplate.smsTemplateTextDtos.forEach(text => (text.selected = contentTemplate.selected));
    this.isAllFavoritesSelected = this.favoriteRows.every(ct => ct.selected);
    if (!contentTemplate.selected) {
      this.markUnselect(contentTemplate);
    }
    this.detectChanges();
  }

  onRowClick(contentTemplate: ContentTemplate): void {
    contentTemplate.selected = !contentTemplate.selected;
    contentTemplate.smsTemplateTextDtos.forEach(text => (text.selected = contentTemplate.selected));
    this.isAllTemplateSelected = this.nonFavoriteRows.every(ct => ct.selected);
    if (!contentTemplate.selected) {
      this.markUnselect(contentTemplate);
    }
    this.detectChanges();
  }

  onFavItemClick(contentTemplate: ContentTemplate, text: ContentTemplateText): void {
    text.selected = !text.selected;
    contentTemplate.selected = contentTemplate.smsTemplateTextDtos.some(text => text.selected);
    this.isAllFavoritesSelected = this.favoriteRows.every(ct => ct.selected);
    if (!text.selected) {
      this.markUnselect(contentTemplate, text);
    }
    this.detectChanges();
  }

  onItemClick(contentTemplate: ContentTemplate, text: ContentTemplateText): void {
    text.selected = !text.selected;
    contentTemplate.selected = contentTemplate.smsTemplateTextDtos.some(text => text.selected);
    this.isAllTemplateSelected = this.nonFavoriteRows.every(ct => ct.selected);
    if (!text.selected) {
      this.markUnselect(contentTemplate, text);
    }
    this.detectChanges();
  }

  onChangeFavorite(event: Event, text: ContentTemplateText, maxFavorites = 0): void {
    event.stopPropagation();
    if (maxFavorites) {
      let countFavorites = 0;
      this.favoriteRows.forEach(fr => (countFavorites += fr.smsTemplateTextDtos.length));
      if (countFavorites > maxFavorites) {
        this.notificationService.error({
          title: 'Live number testing',
          message: `Sorry you cannot select more than ${maxFavorites} favorites.`,
          service: 'LNT',
          requestMessage: '',
          requestCode: 400,
          ts: null,
        });
        return;
      }
    }
    const favValue = !text.userFavourite;
    this.loading = true;
    this.contentTemplateService.updateFavorite(text.id, favValue).subscribe({
      next: res => {
        this.notificationService.success('Favourite mark updated.', 'Content Templates');
        this.update();
      },
      error: error => {
        this.notificationService.error({
          title: 'Content Templates',
          message: 'An error occurred while marking/unmarking the SMS text template as favorite.',
          serviceName: 'NTC',
          requestMessage: error.statusText,
          requestCode: error.status,
          ts: error.timestamp ? error.timestamp : null,
        });
        this.loading = false;
      },
    });
  }

  private markUnselect(contentTemplate: ContentTemplate, text: ContentTemplateText = null) {
    if (text === null) {
      this.selected
        .filter(ct => ct.name === contentTemplate.name)
        .forEach(ct => {
          ct.smsTemplateTextDtos.forEach(t => (t.selected = false));
        });
      return;
    }
    const map = new Map<string, ContentTemplateText>();
    this.selected.forEach(ct => {
      ct.smsTemplateTextDtos.forEach(text => map.set(ContentTemplateService.generateTextId(ct, text), text));
    });
    const id = ContentTemplateService.generateTextId(contentTemplate, text);
    if (map.has(id)) {
      map.get(id).selected = false;
    }
  }

  private detectChanges(): void {
    this.selected = this.selected.filter(ct => ct.smsTemplateTextDtos.some(text => text.selected));

    const selectedMap = new Map<string, ContentTemplate>();
    this.selected.forEach((ct, index) => {
      ct.index = index + 1;
      selectedMap.set(ct.name, ct);
    });

    const updated = this.buildUpdatedSelection(selectedMap);
    this.selectedChange.emit(updated);
  }

  private buildUpdatedSelection(selectedMap: Map<string, ContentTemplate>): ContentTemplate[] {
    const combined = [...this.favoriteRows, ...this.nonFavoriteRows];
    const updatedMap = new Map<string, ContentTemplate>();

    combined.forEach(ct => {
      if (ct.smsTemplateTextDtos.every(text => !text.selected)) return;

      const key = ct.name;
      const texts = ct.smsTemplateTextDtos.map(text => ({ ...text }));

      if (updatedMap.has(key)) {
        updatedMap.get(key).smsTemplateTextDtos.push(...texts);
      } else {
        updatedMap.set(key, {
          id: ct.id,
          name: ct.name,
          description: ct.description,
          smsTemplateTextDtos: texts,
          fromServer: !!ct.id,
          index: selectedMap.get(key)?.index,
        });
      }
    });

    const currentPageSelected = Array.from(updatedMap.values());
    const currentNames = new Set(currentPageSelected.map(ct => ct.name));
    const retained = this.selected.filter(ct => !currentNames.has(ct.name));

    currentPageSelected.forEach(ct => {
      if (!ct.index) {
        ct.index = retained.length + 1;
      }
      retained.push(ct);
    });

    return retained.sort((a, b) => a.index - b.index);
  }

  onChangePage(page) {
    this.page = page;
    this.update();
  }

  onChangeSize(size) {
    if (size === this.size) {
      this.update();
      return;
    }
    this.size = size;
    this.page = 1;
    this.storage.set('sender-list-size', this.size);
    this.update();
  }
}
